import { useEffect, useState } from "react";
import { message } from "antd";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { ClipModal } from "@components/ClipModal/ClipModal";
import { fetchVideoLinkPreview } from "@apis/http/api";
import { CharacterAssetCreateFormRequestCategory } from "@apis/generated/openapi";
import { CharacterEditor } from "./CharacterEditor";
import { CharacterLinkInputStep } from "./LinkInputStep";
import { VideoLibraryPicker } from "./VideoLibraryPicker";
import { useCharacterForm } from "./useCharacterForm";
import type { CreateCharacterEntityModalProps, CreateCharacterMode, CreateCharacterStep } from "./types";

const widthFor = (mode: CreateCharacterMode, step: CreateCharacterStep) => {
  if (step === "editor") return 720;
  if (mode === "videoLink") return 720;
  return 720;
};

const initialStepFor = (mode: CreateCharacterMode): CreateCharacterStep => (mode === "manual" ? "editor" : "input");

/**
 * Create / Edit Character Entity Modal —— Library > Characters > Models/Animals 入口。
 *
 * 三种创建模式（由 CreateMethodDropdown 选择后注入 defaultMode）：
 * - videoLink：粘贴 TikTok / Kalodata / YouTube Shorts 链接，自动取封面作为主图
 * - library：从用户视频库（Generated/Uploaded/Saved）选一条，取封面作为主图
 * - manual：直接打开编辑器，用户手动上传主图与最多 3 张其他视角图
 *
 * 编辑态：传入 characterId 自动拉详情；当前后端 PUT 仅支持改名（图片槽位禁用）。
 */
export const CreateCharacterEntityModal = ({
  open,
  onClose,
  onSuccess,
  characterId,
  defaultCategory = CharacterAssetCreateFormRequestCategory.MODEL,
  defaultMode = "manual",
}: CreateCharacterEntityModalProps) => {
  const isEditMode = !!characterId;
  const [mode, setMode] = useState<CreateCharacterMode>(isEditMode ? "manual" : defaultMode);
  const [step, setStep] = useState<CreateCharacterStep>(isEditMode ? "editor" : initialStepFor(defaultMode));
  const [parsingLink, setParsingLink] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [pickingFromLibrary, setPickingFromLibrary] = useState(false);

  const { form, updateForm, imageFiles, handleFilesChange, uploadFile, setMainImageFromUrl, submitting, submit } =
    useCharacterForm({
      characterId,
      enabled: open,
      defaultCategory,
      onSuccess: () => {
        onSuccess?.();
        onClose();
      },
    });

  useEffect(() => {
    if (isEditMode) {
      setMode("manual");
      setStep("editor");
      return;
    }
    setMode(defaultMode);
    setStep(initialStepFor(defaultMode));
    setLinkError(null);
  }, [open, isEditMode, defaultMode]);

  const handleParseLink = async (url: string, signal?: AbortSignal): Promise<boolean> => {
    setParsingLink(true);
    setLinkError(null);
    try {
      const data = await fetchVideoLinkPreview({ videoLink: url }, { signal });
      const cover = data?.coverImageUrl;
      if (!cover) {
        setLinkError(t("Currently supports links from TikTok, Kalodata, and YouTube Shorts."));
        return false;
      }
      try {
        await setMainImageFromUrl(cover, undefined, signal);
      } catch {
        if (signal?.aborted) return false;
        message.error(t("Failed to load cover image. Please try another video."));
        return false;
      }
      setStep("editor");
      return true;
    } catch {
      if (signal?.aborted) return false;
      setLinkError(t("Currently supports links from TikTok, Kalodata, and YouTube Shorts."));
      return false;
    } finally {
      // aborted 时新请求已接管 spinner 状态，避免老请求 finally 抹掉
      if (!signal?.aborted) setParsingLink(false);
    }
  };

  const handleLibrarySelect = async (video: { id: string; coverImageUrl?: string }) => {
    if (!video.coverImageUrl || pickingFromLibrary) return;
    setPickingFromLibrary(true);
    try {
      await setMainImageFromUrl(video.coverImageUrl);
      setStep("editor");
    } catch {
      message.error(t("Failed to load cover image. Please try another video."));
    } finally {
      setPickingFromLibrary(false);
    }
  };

  const title = isEditMode ? <Trans>Edit Characters Information</Trans> : <Trans>Create Characters</Trans>;

  const renderBody = () => {
    if (step === "input" && mode === "videoLink") {
      return <CharacterLinkInputStep parsing={parsingLink} error={linkError} onParse={handleParseLink} />;
    }
    if (step === "input" && mode === "library") {
      return <VideoLibraryPicker onSelect={handleLibrarySelect} />;
    }
    return (
      <CharacterEditor
        form={form}
        imageFiles={imageFiles}
        submitting={submitting}
        isEditMode={isEditMode}
        onChangeForm={updateForm}
        onFilesChange={handleFilesChange}
        uploadFile={uploadFile}
        onCancel={onClose}
        onSubmit={submit}
      />
    );
  };

  return (
    <ClipModal open={open} onCancel={onClose} title={title} width={widthFor(mode, step)} maskClosable={false}>
      <div className="pt-[8px]">{renderBody()}</div>
    </ClipModal>
  );
};
